import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Trophy, Star, Zap, CheckCircle, RotateCcw, TrendingUp } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';

interface ScoreEvent {
    id: number;
    points: number;
    label: string;
    x: number;
    y: number;
}

export const ScoreSystem = () => {
    const { completeModule } = useAcademyStore();
    const [score, setScore] = useState(0);
    const [combo, setCombo] = useState(0);
    const [multiplier, setMultiplier] = useState(1);
    const [highScore, setHighScore] = useState(0);
    const [events, setEvents] = useState<ScoreEvent[]>([]);
    const [targets, setTargets] = useState<{ id: number; x: number; y: number; points: number; emoji: string }[]>([]);
    const [timeLeft, setTimeLeft] = useState(30);
    const [isPlaying, setIsPlaying] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    // Generate random targets
    const spawnTarget = () => {
        const types = [
            { points: 10, emoji: '🍌' },
            { points: 25, emoji: '🥥' },
            { points: 50, emoji: '⭐' },
            { points: -20, emoji: '💀' },
        ];
        const type = types[Math.floor(Math.random() * types.length)];
        setTargets(prev => [...prev, {
            id: Date.now(),
            x: Math.random() * 350 + 25,
            y: Math.random() * 250 + 25,
            ...type,
        }]);
    };

    // Timer
    useEffect(() => {
        if (!isPlaying || timeLeft <= 0) return;
        const timer = setInterval(() => {
            setTimeLeft(t => {
                if (t <= 1) {
                    setIsPlaying(false);
                    if (score > highScore) setHighScore(score);
                    if (score >= 100 && !completed) {
                        setCompleted(true);
                        completeModule('score-system', 100);
                        setShowSuccess(true);
                    }
                }
                return t - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [isPlaying, timeLeft, score, highScore, completed, completeModule]);

    // Spawn targets
    useEffect(() => {
        if (!isPlaying) return;
        const spawner = setInterval(() => {
            if (targets.length < 8) spawnTarget();
        }, 800);
        return () => clearInterval(spawner);
    }, [isPlaying, targets.length]);

    // Combo decay
    useEffect(() => {
        if (!isPlaying || combo === 0) return;
        const decay = setTimeout(() => {
            setCombo(0);
            setMultiplier(1);
        }, 2000);
        return () => clearTimeout(decay);
    }, [isPlaying, combo]);

    const clickTarget = (target: typeof targets[0]) => {
        // Remove target
        setTargets(prev => prev.filter(t => t.id !== target.id));

        // Calculate points with multiplier
        const points = target.points * multiplier;

        // Update score
        setScore(s => Math.max(0, s + points));

        // Update combo (only for positive points)
        if (target.points > 0) {
            setCombo(c => c + 1);
            setMultiplier(Math.min(4, 1 + Math.floor((combo + 1) / 3)));
        } else {
            setCombo(0);
            setMultiplier(1);
        }

        // Show floating score
        setEvents(prev => [...prev, {
            id: Date.now(),
            points,
            label: points > 0 ? `+${points}` : `${points}`,
            x: target.x,
            y: target.y,
        }]);

        // Remove floating score after animation
        setTimeout(() => {
            setEvents(prev => prev.slice(1));
        }, 1000);
    };

    const startGame = () => {
        setScore(0);
        setCombo(0);
        setMultiplier(1);
        setTimeLeft(30);
        setTargets([]);
        setEvents([]);
        setIsPlaying(true);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-amber-100 to-yellow-50 font-sans pt-24">

            <div className="max-w-5xl mx-auto p-8">
                <div className="text-center mb-6">
                    <h1 className="text-4xl font-black font-comic text-slate-800 mb-2">
                        🏆 Points & Combos
                    </h1>
                    <p className="text-slate-600 text-lg">
                        Good games make scoring <strong>feel amazing</strong>!<br />
                        Click targets fast to build combos and earn more points!
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Game Area */}
                    <div className="lg:col-span-2 bg-white rounded-3xl p-6 shadow-xl">
                        {/* HUD */}
                        <div className="flex justify-between mb-4">
                            <div className="flex gap-4">
                                <div className="bg-amber-100 px-4 py-2 rounded-xl">
                                    <span className="font-bold text-amber-700">Score: {score}</span>
                                </div>
                                <div className={`px-4 py-2 rounded-xl ${multiplier > 1 ? 'bg-purple-100 animate-pulse' : 'bg-slate-100'}`}>
                                    <span className={`font-bold ${multiplier > 1 ? 'text-purple-700' : 'text-slate-500'}`}>
                                        {multiplier}x Combo
                                    </span>
                                </div>
                            </div>
                            <div className={`px-4 py-2 rounded-xl ${timeLeft <= 5 ? 'bg-red-100 animate-pulse' : 'bg-blue-100'}`}>
                                <span className={`font-bold ${timeLeft <= 5 ? 'text-red-700' : 'text-blue-700'}`}>
                                    ⏱️ {timeLeft}s
                                </span>
                            </div>
                        </div>

                        {/* Play Area */}
                        <div className="relative bg-gradient-to-br from-green-100 to-emerald-50 rounded-2xl h-[300px] border-4 border-green-200 overflow-hidden">
                            {!isPlaying && timeLeft === 30 && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <button
                                        onClick={startGame}
                                        className="bg-amber-500 text-white px-8 py-4 rounded-2xl font-bold text-xl hover:bg-amber-600 shadow-lg"
                                    >
                                        Start Game!
                                    </button>
                                </div>
                            )}

                            {!isPlaying && timeLeft === 0 && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                                    <div className="bg-white rounded-3xl p-8 text-center">
                                        <div className="text-4xl mb-2">🏆</div>
                                        <div className="text-2xl font-black text-slate-800">Game Over!</div>
                                        <div className="text-lg text-amber-600 font-bold">Score: {score}</div>
                                        <div className="text-sm text-slate-500 mb-4">High Score: {highScore}</div>
                                        <button
                                            onClick={startGame}
                                            className="bg-amber-500 text-white px-6 py-2 rounded-xl font-bold hover:bg-amber-600"
                                        >
                                            Play Again
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Targets */}
                            <AnimatePresence>
                                {targets.map(target => (
                                    <motion.button
                                        key={target.id}
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        exit={{ scale: 0 }}
                                        onClick={() => clickTarget(target)}
                                        className="absolute text-3xl cursor-pointer hover:scale-125 transition-transform"
                                        style={{ left: target.x, top: target.y }}
                                    >
                                        {target.emoji}
                                    </motion.button>
                                ))}
                            </AnimatePresence>

                            {/* Floating scores */}
                            <AnimatePresence>
                                {events.map(event => (
                                    <motion.div
                                        key={event.id}
                                        initial={{ opacity: 1, y: 0 }}
                                        animate={{ opacity: 0, y: -50 }}
                                        exit={{ opacity: 0 }}
                                        className={`absolute font-black text-2xl ${event.points > 0 ? 'text-green-500' : 'text-red-500'
                                            }`}
                                        style={{ left: event.x, top: event.y }}
                                    >
                                        {event.label}
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>

                        <div className="mt-4 text-center text-slate-500 text-sm">
                            🍌 = 10pts | 🥥 = 25pts | ⭐ = 50pts | 💀 = -20pts (breaks combo!)
                        </div>
                    </div>

                    {/* Explanation */}
                    <div className="space-y-6">
                        <div className="bg-white rounded-3xl p-6 shadow-xl">
                            <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
                                <TrendingUp className="text-amber-500" /> Score Design
                            </h3>

                            <div className="space-y-3 text-sm">
                                <div className="p-3 bg-green-50 rounded-xl border border-green-200">
                                    <div className="font-bold text-green-700">Base Points</div>
                                    <div className="text-green-600">Different actions = different values</div>
                                </div>

                                <div className="p-3 bg-purple-50 rounded-xl border border-purple-200">
                                    <div className="font-bold text-purple-700">Multipliers</div>
                                    <div className="text-purple-600">Combos increase your points!</div>
                                </div>

                                <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
                                    <div className="font-bold text-amber-700">Feedback</div>
                                    <div className="text-amber-600">Show points flying up = feels good!</div>
                                </div>

                                <div className="p-3 bg-red-50 rounded-xl border border-red-200">
                                    <div className="font-bold text-red-700">Risk/Reward</div>
                                    <div className="text-red-600">Bad choices = lose points & combo</div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-amber-50 rounded-2xl p-6 border-2 border-amber-200">
                            <h3 className="font-bold text-amber-800 mb-2">🎓 What Makes Scoring Fun?</h3>
                            <ul className="text-amber-700 text-sm space-y-1">
                                <li>• <strong>Big numbers</strong> feel rewarding</li>
                                <li>• <strong>Combos</strong> reward skilled play</li>
                                <li>• <strong>Risk</strong> makes choices matter</li>
                                <li>• <strong>Feedback</strong> (sounds, animations)</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            {showSuccess && (
                <ModuleSuccess
                    title="Score & Combos"
                    onRetry={() => {
                        setShowSuccess(false);
                        startGame();
                    }}
                >
                    <div className="bg-amber-50 rounded-2xl p-4 text-left mx-auto max-w-sm">
                        <div className="font-bold text-amber-800 mb-2">🏆 High Score Strategy:</div>
                        <ul className="text-amber-700 text-sm space-y-1">
                            <li>• Chain targets quickly just like you did!</li>
                            <li>• <strong>Multipliers</strong> = Huge Points</li>
                            <li>• This is how arcade games hook you in!</li>
                        </ul>
                    </div>
                </ModuleSuccess>
            )}
        </div>
    );
};

export default ScoreSystem;
